/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("WidgetPlaceholderBase", ["WidgetGroupBase"], function (context, cls) {
  /**
   * Base for placeholder widget.
   * This will create a widget that act as an invisible group.
   * It will display only it's content
   * @class WidgetPlaceholderBase
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   * @publicdoc Widgets
   */
  cls.WidgetPlaceholderBase = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.WidgetPlaceholderBase.prototype */ {
      __name: "WidgetPlaceholderBase",
      __virtual: true,

      /**
       * Current active parent id
       * @type {?number}
       */
      _activeParentId: null,

      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        this._activeParentId = 0;
        $super.constructor.call(this, opts);
      },

      /**
       * @inheritDoc
       */
      _initElement: function (initialInformation) {
        this._ignoreLayout = true;
        $super._initElement.call(this, initialInformation);
        this.addClasses("gbc_WidgetPlaceholderBase");
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        $super.destroy.call(this);
        this._activeParentId = null;
      },

      /**
       * Trigger the click event on the child widget being clicked
       * If no widget is found, bubble up the event
       * @param {Event} domEvent Mouse click event
       * @returns {boolean} returns if event must be bubbled to parent DOM widget
       */
      manageMouseClick: function (domEvent) {
        // Get the child widget being clicked
        const clickedElement = domEvent.target,
          clickedWidget = this.getChildFromElement(clickedElement);
        if (clickedWidget && clickedWidget.isEnabled()) {
          return clickedWidget.manageMouseClick(domEvent);
        }
        return true;
      },

      /**
       * Return the child widget associated to this element
       * that belong to this placeholder
       * @param {Element} element The element of the widget to query
       * @returns {classes.WidgetBase|null} The element owner widget
       */
      getChildFromElement: function (element) {
        return this._children.find((widget) => widget.getElement().contains(element));
      },

      /**
       * @inheritDoc
       */
      addChildWidget: function (widget, options) {
        $super.addChildWidget.call(this, widget, options);
        widget.when(context.constants.widgetEvents.visibilityChange, () => this._updateContainerAutoWidgetsVisibility());
        this._updateContainerAutoWidgetsVisibility();
      },

      /**
       * Inform container if auto widgets are visible or not
       * @private
       */
      _updateContainerAutoWidgetsVisibility: function () {
        for (const widget of this._children.values()) {
          if (widget.isDestroyed()) {
            continue;
          }

          if (!widget.getParentWidget()) {
            continue;
          }

          if (widget.isVisible() && widget.getElement().parentNode) {
            this.setHidden(false);
            return;
          }
        }

        this.setHidden(true);
      },

      /**
       * @inheritDoc
       */
      removeChildWidget: function (widget) {
        $super.removeChildWidget.call(this, widget);
        this._updateContainerAutoWidgetsVisibility();
      },

      /**
       * Set the active parent id (example: Menu id)
       * @param {number} parentId
       */
      setActiveParentId: function (parentId) {
        this._refreshChild(parentId);
      },

      /**
       * Get the current active parent id
       * @return {number}
       */
      getActiveParentId: function () {
        return this._activeParentId;
      },

      /**
       * Refresh the DOM content of the placeholder
       * @param parentId the widgets of this parent are attached to the DOM
       * @private
       */
      _refreshChild: function (parentId) {
        if (this._activeParentId === parentId) {
          return;
        }

        //Update placeholder content according to the new active widget
        const newParentId = parentId;
        const oldParentId = this._activeParentId;

        for (const widget of this._children) {
          if (widget.isDestroyed()) {
            continue;
          }

          const realWidget = widget.getAUIWidget();
          const parentMenuWidget = realWidget.getParentWidget(cls.MenuWidget);

          if (parentMenuWidget?.getAuiTag() === oldParentId) {
            widget.getElement().remove();
          }
        }

        for (const widget of this._children) {
          if (widget.isDestroyed()) {
            continue;
          }

          const realWidget = widget.getAUIWidget();
          const parentMenuWidget = realWidget.getParentWidget(cls.MenuWidget);

          if (parentMenuWidget?.getAuiTag() === newParentId) {
            this.getElement().appendChild(widget.getElement());
          }
        }

        this._activeParentId = parentId;

        this._updateContainerAutoWidgetsVisibility();
      },

      /**
       * Set Default TTF color
       * @param {string} color - rgb formatted or css name
       */
      setDefaultTTFColor: function (color) {
        this._applyOnChildren("setDefaultTTFColor", color);
      },

      /**
       * @inheritDoc
       */
      setFontSize: function (size) {
        this._applyOnChildren("setFontSize", size);
      },

      /**
       * @inheritDoc
       */
      setFontStyle: function (style) {
        this._applyOnChildren("setFontStyle", style);
      },

      /**
       * @inheritDoc
       */
      setNoBorder: function (noBorder) {
        this._applyOnChildren("setNoBorder", noBorder);
      },

      /**
       * @inheritDoc
       */
      setButtonType: function (buttonType) {
        this._applyOnChildren("setButtonType", buttonType);
      },

      /**
       * @inheritDoc
       */
      setFontColor: function (color) {
        this._applyOnChildren("setFontColor", color);
      },

      /**
       * @inheritDoc
       */
      setWrapPolicy: function (format) {
        this._applyOnChildren("setWrapPolicy", format);
      },

      /**
       * Apply the function in parameter to the child.
       * If it exists in child class definition
       * Else it will pass without triggering any error
       * @param {Function} func The function to apply on the child
       * @param  {...any} params The parameters to give to the applied functions
       */
      _applyOnChildren(func, ...params) {
        this._children.forEach((child) => {
          if (child[func]) {
            child[func](...params);
          }
        });
      }
    };
  });
});
